import React, { useState, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { sanitizeClonedColors } from './mapPrintColorSanitizer';
import { renderLegendStrip } from './mapPrintLegend';
import { getActiveLegends } from '../LegendControl/legendModel';
import { useLayerDrawer } from '../../layerDrawer';
import './MapPrinting.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { useTools } from '../../../context/ToolContext';

interface MapPrintingProps {
  map: mapboxgl.Map;
  onClose?: () => void; // When provided (from ToolHost), capture runs immediately
}

const MapPrinting: React.FC<MapPrintingProps> = ({ map, onClose }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const { openTool } = useTools();
  const { getEnabledLayers } = useLayerDrawer();

  // When rendered by ToolHost (onClose is provided), capture immediately
  useEffect(() => {
    if (onClose && map) {
      captureMap().then(() => {
        onClose();
      });
    }
  }, [onClose, map]);

  const captureMap = async () => {
    console.log('Capture map clicked');
    if (!map) return;

    // Load the PDF/canvas libraries on demand so the ~210 KB jspdf+html2canvas
    // chunk stays out of the initial app bundle (only printing needs it).
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('html2canvas'),
    ]);

    // Wait for all layers to finish rendering before capturing
    map.triggerRepaint();
    await new Promise<void>((resolve) => {
      map.once('idle', () => resolve());
    });

    const mapCanvas = map.getCanvas();
    const mapContainer = map.getContainer();

    // Step 1: Capture the MapBox GL canvas (base map with all tile layers)
    const mapboxImgData = mapCanvas.toDataURL('image/png');

    // Step 2: Capture DOM overlays using html2canvas (markers, popups, etc.)
    // Ignore the canvas element itself since we captured it directly
    const domOverlayCanvas = await html2canvas(mapContainer, {
      backgroundColor: null, // Transparent background
      ignoreElements: (element) => {
        // Ignore the MapBox canvas - we already have it captured
        if (element.tagName === 'CANVAS') return true;
        // Ignore map controls (zoom buttons, etc.) - they're UI, not map content
        if (element.classList?.contains('mapboxgl-ctrl')) return true;
        return false;
      },
      logging: false,
      useCORS: true,
      scale: window.devicePixelRatio || 1, // Match device pixel ratio
      onclone: (_clonedDoc, clonedElement) => {
        // #621: html2canvas 1.x cannot parse color()/color-mix() computed
        // values produced by the theme's color-mix tokens. Rewrite them to
        // rgb in the clone so the capture doesn't throw and abort the export.
        sanitizeClonedColors(clonedElement);
      },
    });

    // Step 3: Create composite canvas
    const compositeCanvas = document.createElement('canvas');
    compositeCanvas.width = mapCanvas.width;
    compositeCanvas.height = mapCanvas.height;
    const compositeCtx = compositeCanvas.getContext('2d');
    if (!compositeCtx) return;

    // Draw MapBox canvas first (base layer)
    const mapboxImage = new Image();
    mapboxImage.src = mapboxImgData;
    await new Promise((resolve) => {
      mapboxImage.onload = resolve;
    });
    compositeCtx.drawImage(mapboxImage, 0, 0);

    // Draw DOM overlays on top (markers, live layers, etc.)
    // Scale to match if html2canvas captured at different dimensions
    compositeCtx.drawImage(
      domOverlayCanvas,
      0, 0, domOverlayCanvas.width, domOverlayCanvas.height,
      0, 0, mapCanvas.width, mapCanvas.height
    );

    // Step 4: Build a complete legend strip from the shared legend model and
    // place it beside the map. This replaces the old DOM screenshot of the
    // legend widget (which was scroll-clipped, included the panel chrome, and
    // broke the export when html2canvas hit unsupported color() values).
    const legendStrip = await renderLegendStrip(getActiveLegends(getEnabledLayers()));
    const legendGap = legendStrip ? 8 : 0;
    const legendWidth = legendStrip ? legendStrip.width + legendGap : 0;

    // Calculate the map scale
    const zoom = map.getZoom();
    const earthCircumference = 40075016.686; // in meters
    const tileSize = 256; // in pixels
    const scale = earthCircumference / (Math.pow(2, zoom) * tileSize);

    // Create a new canvas to add a border and scale
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions — reserve a right-hand strip for the legend so it
    // never overlaps the map, plus a 5px border and bottom space for the scale.
    canvas.width = compositeCanvas.width + legendWidth + 10;
    canvas.height = Math.max(compositeCanvas.height, legendStrip?.height ?? 0) + 50;

    // Draw the border
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw the composite image on top of the border
    ctx.drawImage(compositeCanvas, 5, 5);

    // Draw the legend strip to the right of the map.
    if (legendStrip) {
      ctx.drawImage(legendStrip, 5 + compositeCanvas.width + legendGap, 5);
    }

    // Add the map scale
    ctx.font = '20px Arial';
    ctx.fillStyle = 'white';

    let scaleText = `Scale: 1:${Math.round(scale)}`;
    let mapName = 'Captured from IntelliFire EasyMap on ' + new Date().toLocaleString();



    ctx.fillText(`${scaleText} - ${mapName}`, 10, canvas.height - 20);

    const borderedImgData = canvas.toDataURL('image/png');
    setImageUrl(borderedImgData);

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });
    pdf.addImage(borderedImgData, 'PNG', 0, 0, canvas.width, canvas.height);
    const pdfData = pdf.output('datauristring');

    // Open the image in a new tab
    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>Map Image</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                text-align: center;
                margin: 0;
                padding: 0;
              }
              .container {
                position: relative;
                display: inline-block;
              }
              img {
                max-width: 100%;
                height: auto;
                box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.313);
              }
              .download-button {
                position: absolute;
                top: 100px; /* Adjust the top position as needed */
                left: 50%;
                transform: translateX(-50%);
                background-color: #007bff;
                color: white;
                text-decoration: none;
                border-radius: 4px;
                padding: 10px 20px;
                display: inline-block;
              }
              .download-button:hover {
                background-color: #0056b3;
              }
              .hidden {
                display: none;
              }
              @media print {
                .download-button {
                  display: none;
                }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <img src="${borderedImgData}" alt="Map" />
              <a href="${pdfData}" download="map.pdf" class="download-button">
              You can copy/paste this map image -or- you can save it, -or- you 
              Print it - Ctrl + P (Win) or Cmd + P (Mac) -or- Click to Download PDF
              </a>
            </div>
          </body>
        </html>
      `);
    }
  };

  // If rendered by ToolHost (onClose provided), don't render button
  if (onClose) {
    return null;
  }

  return (
    <div className="map-printing">
      <button onClick={() => openTool('printMap')} className="control-button" title='Save, Print or export this map'>
        <FontAwesomeIcon icon={faPrint} />
      </button>
    </div>
  );
};

export default MapPrinting;
